import { useEffect, useRef, useState } from 'react';
import type { CityDto } from '@waylo/shared';
import { useCityResults } from '../lib/useCityResults';

interface Props {
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Language-gated scope: 'RS' | 'HR' | 'BA' | 'all'. */
  country: string;
  /** Cities that should never appear in suggestions (typically the route endpoints). */
  excludeIds?: string[];
  maxSelected?: number;
  nameKey: 'nameEn' | 'nameSr';
  placeholder?: string;
  emptyText?: string;
  /** City objects already chosen (so chips can show names without the full list). */
  initialCities?: CityDto[];
}

/**
 * Typeahead multi-picker for via-stops, backed by the server search endpoint.
 * Remembers picked CityDtos internally so chips render names without shipping
 * the whole catalogue.
 */
export function CitySearchInput({
  selectedIds,
  onChange,
  country,
  excludeIds = [],
  maxSelected = 8,
  nameKey,
  placeholder = 'Type to search…',
  emptyText = 'No matches.',
  initialCities = [],
}: Props): JSX.Element {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // id -> CityDto for everything we've seen, so selected chips can show names.
  const [known, setKnown] = useState<Record<string, CityDto>>(() =>
    Object.fromEntries(initialCities.map((c) => [c.id, c])),
  );

  const { results, loading } = useCityResults(q, country, open);
  useEffect(() => {
    if (results.length === 0) return;
    setKnown((prev) => {
      const next = { ...prev };
      for (const c of results) next[c.id] = c;
      return next;
    });
  }, [results]);

  const exclude = new Set([...excludeIds, ...selectedIds]);
  const matches = results.filter((c) => !exclude.has(c.id));
  const selectedCities = selectedIds.map((id) => known[id]).filter((c): c is CityDto => Boolean(c));
  const limitReached = selectedIds.length >= maxSelected;

  function pick(c: CityDto): void {
    if (selectedIds.length >= maxSelected) return;
    setKnown((prev) => ({ ...prev, [c.id]: c }));
    onChange([...selectedIds, c.id]);
    setQ('');
    inputRef.current?.focus();
  }
  function remove(id: string): void {
    onChange(selectedIds.filter((i) => i !== id));
  }

  return (
    <div>
      {selectedCities.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedCities.map((c, idx) => (
            <button
              key={c.id}
              type="button"
              onClick={() => remove(c.id)}
              className="inline-flex items-center gap-1 rounded-pill bg-[var(--border-medium)] px-3 py-1 text-xs font-medium text-tg-text"
              aria-label={`Remove ${c[nameKey]}`}
            >
              <span>
                {idx + 1}. {c[nameKey]}
              </span>
              <span aria-hidden className="text-tg-hint">
                ×
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={limitReached ? `Max ${maxSelected} reached` : placeholder}
          disabled={limitReached}
          className={
            'w-full rounded-input border border-[var(--border-light)] bg-tg-bg px-3 py-2 text-sm text-tg-text outline-none focus:border-brand-green ' +
            (limitReached ? 'opacity-50' : '')
          }
        />

        {open && !limitReached && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-input border border-[var(--border-light)] bg-tg-bg shadow-md">
            {matches.length === 0 ? (
              <p className="px-3 py-2 text-xs text-tg-hint">{loading ? '…' : emptyText}</p>
            ) : (
              matches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(c);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-tg-text transition hover:bg-[var(--border-light)]"
                >
                  {c[nameKey]}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <p className="mt-1 text-xs text-tg-hint">
        {selectedIds.length}/{maxSelected} selected
      </p>
    </div>
  );
}
